import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const parseUser = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Провайдер контекста авторизации — оборачивает всё приложение
export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  // user: { id, email, is_admin, force_password_change }
  const [user, setUser] = useState(() => parseUser(localStorage.getItem('authUser')));
  const isAuthenticated = !!token;

  // Синхронизация с интерцепторами api/http.js:
  // - auth:token-refreshed — сервер выдал свежий токен (скользящее продление сессии)
  // - auth:session-expired — токен истёк/отозван, сессия уже очищена в localStorage;
  //   сбрасываем state, ProtectedRoute перенаправит на /login
  useEffect(() => {
    const onTokenRefreshed = (e) => setToken(e.detail);
    const onSessionExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:token-refreshed', onTokenRefreshed);
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => {
      window.removeEventListener('auth:token-refreshed', onTokenRefreshed);
      window.removeEventListener('auth:session-expired', onSessionExpired);
    };
  }, []);

  // Сохраняем токен и данные пользователя при входе/регистрации
  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
  };

  // Обновляем поля user без смены токена (например, после смены пароля)
  const updateUser = (updatedFields) => {
    const newUser = { ...user, ...updatedFields };
    localStorage.setItem('authUser', JSON.stringify(newUser));
    setUser(newUser);
  };

  // Удаляем токен при выходе
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, isAuthenticated, login, updateUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Хук для удобного использования контекста в компонентах
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
